





























import { Component, Vue } from 'vue-property-decorator';
import gql from 'graphql-tag';
import CenterList from '@/components/CenterList.vue';
import LaneSelect from '@/components/LaneSelect.vue';
import Modal from '@/components/Modal.vue';
import LinkSession from '@/components/LinkSession.vue';
import StartSession from '@/components/StartSession.vue';
import EndSession from '@/components/EndSession.vue';
import store from '@/store';

type SelectMode = 'watch' | 'play' | 'manage' | 'default';

type LaneConfig = 'CROSS_LANE' | 'SINGLE_LANE' | 'MULTI_LANE';

interface ISession {
  id: string;
  startTime: string;
  endTime: string;
  laneConfig: LaneConfig;
  activeLanes: number[];
}

interface ICenter {
  id: string;
  centerCode: string;
  name: string;
  lanes: number;
  activeSessions: ISession[];
}

const UserQuery = gql`
  {
    user {
      id
      bowlingNickname
      activeSessions {
        bowlingPosition
        center {
          id
        }
        lanes
      }
    }
  }
`;

const CenterQuery = gql`
  query Center($id: ID) {
    center(id: $id) {
      id
      centerCode
      name
      country
      city
      stateOrProvince
      postalCode
      phone
      lanes
      activeSessions {
        id
        startTime
        endTime
        activeLanes
        laneConfig
      }
    }
  }
`;


const ApolloOptions = Vue.extend({
  apollo: {
    user: {
      query: UserQuery,
      skip() {
        return !store.getters['auth/authenticated'];
      },
    },
    center: {
      query: CenterQuery,
      variables() {
        return { id: this.centerId };
      },
      skip() {
        return !this.centerId;
      },
    },
  },
});

@Component<SelectGame>({
  components: {
    CenterList,
    LaneSelect,
    Modal,
    LinkSession,
    StartSession,
    EndSession,
  },
  watch: {
    '$route.query.centerId'(centerId?: string) {
      (this as SelectGame).centerId = centerId || null;
    },
  },
})
export default class SelectGame extends ApolloOptions {
  public center?: ICenter;
  public lane?: number;

  public showEndSession = false;
  public showStartSession = false;
  public showJoinGame = false;

  public user: any = null;
  public centerId: string | null = null;

  get mode(): SelectMode {
    const m = this.$route.query.mode;

    if (!m || ['watch', 'play', 'manage'].indexOf(m) < 0) {
      return 'default';
    }

    return m as SelectMode;
  }

  public created() {
    switch (this.mode) {
      case 'watch': break;
      case 'manage':
      case 'play': {
        if (!store.getters['auth/authenticated']) {
          return this.unauthorized();
        }
        break;
      }
      default:
        this.$router.back();
        break;
    }
  }

  public mounted() {
    this.centerId = this.$route.query.centerId || null;
  }

  public unauthorized() {
    this.$router.back();
  }

  public onCenterSelected(centerId: string) {
    this.centerId = centerId;
    this.$apollo.queries.center.skip = false;

    this.$router.push(
      `/selectGame?mode=${this.mode}&centerId=${this.centerId}`,
    );
  }

  public onLaneSelected(lane: number) {
    const session = this.getSession(lane);
    this.lane = lane;

    if (this.mode === 'manage') {
      return this.manageSessions(lane, session);
    }

    // Watch mode only works for already started games
    if (this.mode === 'watch' && session) {
      this.watchGame();
    }

    if (this.mode === 'play') {
      this.playGame();
    }
    // if there is one session, go to it
    // if (session) {
    //   if (this.mode === 'watch') {
    //     return this.watchGame();
    //   } else {
    //     return this.playGame();
    //   }
    // } else {
    //   this.manageSessions(lane, undefined);
    // }
  }

  public onSessionStarted() {
    this.showStartSession = false;
    this.lane = undefined;

    this.$apollo.queries.center.refetch();
  }

  public onSessionEnded() {
    this.showEndSession = false;
    this.lane = undefined;

    this.$apollo.queries.center.refetch();
  }

  public onSessionLinked() {
    if (!this.center || !this.lane) { return; }

    this.$router.push(`/footbowl/${this.center.centerCode}/lane/${this.lane}`);
  }

  public getLanePair(lane: number) {
    return [lane, lane % 2 === 1 ? lane + 1 : lane - 1];
  }

  public watchGame() {
    if (!this.center || !this.lane) { return; }

    this.$router.push(`/footbowl/${this.center.centerCode}/lane/${this.lane}`);
  }

  public playGame() {
    if (!this.center || !this.lane) { return; }

    // Check if this user is already a part of this game
    if (this.user && this.user.activeSessions) {
      const exists = this.user.activeSessions.some(
        (s: any) =>
          s.center.id === (this.center as any).id &&
          s.lanes.indexOf(this.lane) > -1,
      );
      if (exists) {
        return this.onSessionLinked();
      }
    }

    this.showJoinGame = true;
  }

  public manageSessions(lane: number, session?: ISession) {
    if (session) {
      this.showEndSession = true;
    } else {
      this.showStartSession = true;
    }
  }

  public getSession(lane: number) {
    return this.activeSessions.find((s) =>
      s.activeLanes.indexOf(lane) > -1,
    );
  }

  public isLaneActive(lane: number) {
    return this.activeLanes.indexOf(lane) > -1;
  }

  public getSessionDictionary(lane: number) {
    return this.getLanePair(lane)
      .map((l) => this.getSession(l))
      .reduce((obj, s) => s ? ({...obj, [s.id]: s.activeLanes}) : obj, {} as {[id: string]: number[]});
  }

  public isPairedActive(lane: number) {
    const paired = lane % 2 === 0 ? lane - 1 : lane + 1;
    return !this.isLaneActive(paired);
  }

  get activeLanes() {
    return this.activeSessions.reduce((prev, s) => [...prev, ...s.activeLanes], [] as number[]);
  }

  get activeSessions() {
    if (!this.center) { return []; }

    return this.center.activeSessions;
  }
}
